import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { StatusIndicator } from '@/modules/wms/components/common/StatusIndicator';
import { wmsAPI, receivingAPI } from '@/shared/services/api';
import { SortableHeader, useSortableData } from '@/shared/components/ui/sortable-table';
import {
  Search,
  Filter,
  Download,
  Plus,
  Package,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ArrowUpDown,
  MoreHorizontal,
  MapPin,
  Thermometer,
  Loader2 } from
'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';
import { toast } from 'sonner';

const statusFilters = ['all', 'in_stock', 'low_stock', 'out_of_stock'];
const zoneFilters = ['All Zones', 'Cold Storage', 'General Storage', 'Cold Chain', 'Ambient'];

// Calculate expiry warning threshold once (90 days from now)
const getExpiryWarningDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 90);
  return date;
};

const EXPIRY_WARNING_DATE = getExpiryWarningDate();

const isNearExpiry = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < EXPIRY_WARNING_DATE;
};

export const Inventory = () => {const { t } = useTranslation("wms");
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [zoneFilter, setZoneFilter] = useState('All Zones');
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalSkus: 0, inStock: 0, lowStock: 0, outOfStock: 0, totalValue: 0 });

  // Fetch inventory from backend
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [inventoryData, locationsData] = await Promise.all([
        wmsAPI.getInventory(),
        receivingAPI.getLocations().catch(() => [])]
        );
        setInventory(inventoryData || []);
        setLocations(locationsData || []);

        const inv = inventoryData || [];
        setStats({
          totalSkus: inv.length,
          inStock: inv.filter((i) => i.status === 'in_stock' || i.quantity > (i.reorder_level || 0)).length,
          lowStock: inv.filter((i) => i.status === 'low_stock' || i.quantity > 0 && i.quantity <= (i.reorder_level || 0)).length,
          outOfStock: inv.filter((i) => i.status === 'out_of_stock' || i.quantity === 0).length,
          totalValue: inv.reduce((sum, i) => sum + (i.quantity || 0) * (i.unit_cost || i.unitCost || 0), 0)
        });
      } catch (error) {
        console.error('Failed to fetch inventory:', error);
        toast.error('Failed to load inventory');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Apply sorting
  const { sortedData, sortConfig, requestSort } = useSortableData(inventory);

  const filteredInventory = sortedData.filter((item) => {
    const productName = item.productName || item.product_name || item.sku || '';
    const sku = item.sku || '';
    const matchesSearch = productName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sku.toLowerCase().includes(searchTerm.toLowerCase());
    const itemStatus = item.status || (item.quantity === 0 ? 'out_of_stock' : item.quantity <= (item.reorder_level || 0) ? 'low_stock' : 'in_stock');
    const matchesStatus = statusFilter === 'all' || itemStatus === statusFilter;
    const zone = item.zone || '';
    const matchesZone = zoneFilter === 'All Zones' || zone === zoneFilter;
    return matchesSearch && matchesStatus && matchesZone;
  });

  return (
    <div className="space-y-6" data-testid="wms-inventory">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("inventory", "Inventory")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageStockLevels", "Manage stock levels, locations, and inventory movements")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("cycleCount", "Cycle Count")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">
            <Plus className="h-4 w-4" />{t("addItem", "Add Item")}

          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalSkus", "Total SKUs")}</p>
                <p className="text-2xl font-bold">{stats.totalSkus}</p>
              </div>
              <Package className="h-8 w-8 text-blue-500/40" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("inStock", "In Stock")}</p>
                <p className="text-2xl font-bold text-green-600">{stats.inStock}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("lowStock", "Low Stock")}</p>
                <p className="text-2xl font-bold text-amber-600">{stats.lowStock}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-amber-500/40" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("outOfStock", "Out of Stock")}</p>
                <p className="text-2xl font-bold text-red-600">{stats.outOfStock}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="card-elevated">
          <CardContent className="p-4">
            <div>
              <p className="text-sm text-muted-foreground">{t("totalValue", "Total Value")}</p>
              <p className="text-2xl font-bold">${stats.totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Search */}
      <Card className="card-elevated">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by SKU or product name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="inventory-search" />
              
            </div>
            <div className="flex gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Filter className="h-4 w-4" />{t("status", "Status:")}
                    {statusFilter === 'all' ? 'All' : statusFilter.replace('_', ' ')}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {statusFilters.map((status) =>
                  <DropdownMenuItem key={status} onClick={() => setStatusFilter(status)}>
                      {status === 'all' ? 'All' : status.replace('_', ' ')}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <MapPin className="h-4 w-4" />{t("zone", "Zone:")}
                    {zoneFilter}
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {zoneFilters.map((zone) =>
                  <DropdownMenuItem key={zone} onClick={() => setZoneFilter(zone)}>
                      {zone}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Inventory Table */}
      <Card className="card-elevated">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <SortableHeader column="sku" label="SKU" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="productName" label="Product Name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="location" label="Location" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="zone" label="Zone" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="quantity" label="Quantity" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader column="available" label="Available" sortConfig={sortConfig} onSort={requestSort} align="right" />
                  <SortableHeader column="lot_number" label="Lot #" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="expiry_date" label="Expiry" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableHeader column="status" label="Status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="text-right py-4 px-4 text-sm font-medium text-muted-foreground">{t("actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {loading ?
                <tr>
                    <td colSpan={10} className="py-8 text-center">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                      <p className="text-sm text-muted-foreground mt-2">{t("loadingInventory", "Loading inventory...")}</p>
                    </td>
                  </tr> :
                filteredInventory.length === 0 ?
                <tr>
                    <td colSpan={10} className="py-8 text-center">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground/30 mb-4" />
                      <p className="text-sm text-muted-foreground">{t("noInventoryItems", "No inventory items found")}</p>
                    </td>
                  </tr> :
                filteredInventory.map((item) => {
                  const itemId = item.id || `${item.sku}-${item.location}`;
                  const productName = item.productName || item.product_name || item.sku;
                  const location = item.location || item.bin_location || '-';
                  const zone = item.zone || '-';
                  const quantity = item.quantity || 0;
                  const availableQty = item.availableQty || item.available_qty || quantity;
                  const reorderLevel = item.reorderLevel || item.reorder_level || 0;
                  const lotNumber = item.lotNumber || item.lot_number || '-';
                  const expiryDate = item.expiryDate || item.expiry_date;
                  const itemStatus = item.status || (quantity === 0 ? 'out_of_stock' : quantity <= reorderLevel ? 'low_stock' : 'in_stock');

                  return (
                    <tr key={itemId} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm font-medium">{item.sku}</span>
                      </td>
                      <td className="py-4 px-4">
                        <div>
                          <p className="font-medium text-sm">{productName}</p>
                          <p className="text-xs text-muted-foreground">{item.category || '-'}</p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5">
                          <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono text-sm">{location}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-1.5">
                          {zone.includes('Cold') && <Thermometer className="h-3.5 w-3.5 text-blue-500" />}
                          <span className="text-sm">{zone}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="font-medium">{quantity.toLocaleString()}</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={availableQty < reorderLevel ? 'text-red-600 font-medium' : ''}>
                          {availableQty.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="font-mono text-sm text-muted-foreground">{lotNumber}</span>
                      </td>
                      <td className="py-4 px-4">
                        {expiryDate ?
                        <span className={`text-sm ${
                        isNearExpiry(expiryDate) ?
                        'text-amber-600 font-medium' :
                        ''}`
                        }>
                            {expiryDate}
                          </span> :
                        '-'}
                      </td>
                      <td className="py-4 px-4">
                        <StatusIndicator status={itemStatus} />
                      </td>
                      <td className="py-4 px-4 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>{t("viewDetails", "View Details")}</DropdownMenuItem>
                            <DropdownMenuItem>{t("adjustStock", "Adjust Stock")}</DropdownMenuItem>
                            <DropdownMenuItem>{t("moveLocation", "Move Location")}</DropdownMenuItem>
                            <DropdownMenuItem>{t("viewHistory", "View History")}</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>);

                })}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          <div className="flex items-center justify-between p-4 border-t border-border">
            <p className="text-sm text-muted-foreground">{t("showing", "Showing")}
              {filteredInventory.length}{t("of", "of")}{inventory.length}{t("items", "items")}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled>{t("previous", "Previous")}</Button>
              <Button variant="outline" size="sm" disabled>{t("next", "Next")}</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Zone Capacity */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("zoneCapacityOverview", "Zone Capacity Overview")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {locations.length === 0 ?
            <div className="col-span-full py-4 text-center text-muted-foreground">{t("noZoneData", "No zone data available")}

            </div> :
            locations.map((zone, index) => {
              const used = zone.used || zone.current_qty || 0;
              const capacity = zone.capacity || zone.max_capacity || 100;
              const percentage = Math.round(used / capacity * 100);
              return (
                <div key={zone.id || index} className="p-4 rounded-lg border border-border bg-muted/20">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium truncate">{zone.name || zone.location_code || `Zone ${index + 1}`}</span>
                    {(zone.type === 'cold' || zone.zone_type === 'cold') && <Thermometer className="h-4 w-4 text-blue-500" />}
                    {(zone.type === 'frozen' || zone.zone_type === 'frozen') && <Thermometer className="h-4 w-4 text-cyan-500" />}
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden mb-2">
                    <div
                      className={`h-full rounded-full ${
                      percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-amber-500' : 'bg-green-500'}`
                      }
                      style={{ width: `${percentage}%` }} />
                    
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{used.toLocaleString()} / {capacity.toLocaleString()}</span>
                    <span className={percentage > 90 ? 'text-red-600 font-medium' : ''}>{percentage}%</span>
                  </div>
                  {zone.temperature &&
                  <p className="text-xs text-muted-foreground mt-1">{zone.temperature}</p>
                  }
                </div>);

            })}
          </div>
        </CardContent>
      </Card>
    </div>);

};